import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getI18n, useTranslation } from "react-i18next";
import Button from "../../Button/Button";
import getClasses from "../../../utils/getClasses";
import { Template } from "../../../pages/Survey/Template/Template";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { Category, translations } from "../../../models/SurveyModels";
import FormSelect, { IOption } from "../FormInput/FormSelect/FormSelect";
import { useNavigate } from "react-router-dom";
import { RoutesRouter } from "../../../models/Routes";
import Step3Issue from "./Step3Issue";
import { useEndpoints } from "../../../hooks/useEndpoints";
import {
  IssuePayload,
  PopulatedIssue,
  SEVERITY,
  issueModel,
  severity,
} from "../../../models/IssueModels";
import Translations, { IPopulated } from "../Translations";
import Skeleton from "react-loading-skeleton";
import { PopulatedResource, ROLE_FILTER } from "../../../models/LibraryModels";
import CustomRadio from "../FormInput/CustomRadio/CustomRadio";

interface IFormTemplate {
  issueData?: PopulatedIssue;
  onSumbitForm: (data: IssuePayload) => void;
  status: number;
  isLoading: boolean;
  id?: string;
  categories: IOption[];
  onSubmitCreate: (data: IssuePayload) => void;
}
const FormCreateIssue: React.FC<IFormTemplate> = ({
  issueData,
  onSumbitForm,
  status,
  isLoading,
  onSubmitCreate,
  categories,
  id,
}: IFormTemplate) => {
  const {
    reset,
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    getValues,
    unregister,
  } = useForm<IssuePayload>({
    mode: "all",
  });
  const controls = useWatch({
    control,
  });

  const { t } = useTranslation();

  const formState = useFormState({
    control,
  });

  const options = [
    {
      label: `${t("createLibrary.libraryData.domain.domains.HR")}`,
      value: ROLE_FILTER.HR,
    },
    {
      label: `${t("createLibrary.libraryData.domain.domains.Manager")}`,
      value: ROLE_FILTER.MANAGER,
    },
    {
      label: `${t("createLibrary.libraryData.domain.domains.All")}`,
      value: ROLE_FILTER.EMPLOYEE,
    },
  ];

  const [selectedValue, setSelectedValue] = useState<ROLE_FILTER>(
    ROLE_FILTER.HR
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      setSelectedValue(value as ROLE_FILTER);
      setValue("role", value as ROLE_FILTER);
    },
    [setValue]
  );
  const navigate = useNavigate();

  const [translationsForIndex, setTranslations] = useState<IOption[][]>([
    translations,
  ]);
  const [selectedTranslations, setSelectedTranslations] = useState<string[]>(
    []
  );
  const handleAddLanguage = () => {
    const newTranslation = {
      title: "",
      description: "",
      language: "",
    };
    setValue(
      `translations.${controls.translations?.length || 0}`,
      newTranslation
    );
    setIssue((oldValue) => ({
      ...oldValue!,
      translations: [...(oldValue?.translations || []), newTranslation],
    }));
    setTranslations((prevTranslations) => [
      ...prevTranslations,
      translations.filter((item) =>
        selectedTranslations.every((term) =>
          Object.values(item).some(
            (value) => !value.toLowerCase().includes(term.toLowerCase())
          )
        )
      ),
    ]);
  };
  const removeLanguage = (index: number) => {
    const newTranslations = [...issue.translations];
    newTranslations.splice(index, 1);
    setIssue((oldValue) => ({
      ...oldValue,
      translations: newTranslations,
    }));
    setSelectedTranslations((old) => [...old, ...old.splice(index, 1)]);
    setTranslations((old) => [...old, ...old.splice(index, 1)]);
    unregister(`translations.${index}`);
  };

  const [currentStep, setCurrentStep] = useState(1);
  const { getCategories } = useEndpoints();
  const [arrayFilterSubCategory, setArrayFilterSubCategory] = useState<
    Category[]
  >([]);
  const [issue, setIssue] = useState<IPopulated>(issueData || issueModel);

  useEffect(() => {
    getCategories({
      language: getI18n().language.includes("it") ? "ITALIAN" : "ENGLISH",
    }).then((resp) => {
      setArrayFilterSubCategory(resp);
    });
  }, [getCategories, t]);

  const onSubmit = (data: IssuePayload) => {
    setCurrentStep((prevStep) => prevStep + 1);
    id ? onSumbitForm(data) : currentStep === 3 && onSubmitCreate(data);
  };

  useEffect(() => {
    if (id)
      currentStep > 3 && status === 200
        ? navigate(RoutesRouter.issue)
        : currentStep > 3 && setCurrentStep(3);
  }, [currentStep, id, navigate, status]);

  useEffect(() => {
    if (issueData) {
      reset();
      issueData.categoryId && setValue("categoryId", issueData.categoryId);
      setIssue(issueData);
      issueData?.severity && setValue("severity", issueData.severity);
      issueData.translations &&
        issueData.translations.forEach(
          ({ language, description, title }, index) => {
            setValue(`translations.${index}`, { language, description, title });
            setSelectedTranslations((old) => {
              if (old.includes(language)) return [...old];
              else return [...old!, language];
            });
          }
        );
      setTranslations((prevTranslations) => {
        const newTranslations = translations.filter((item) =>
          selectedTranslations.every((term) =>
            Object.values(item).some(
              (value) => !value.toLowerCase().includes(term.toLowerCase())
            )
          )
        );
        const isDuplicate = prevTranslations.some((prevItem) => {
          if (prevItem.length !== newTranslations.length) {
            return false;
          }
          return prevItem.every((prevObj, index) => {
            const newObj = newTranslations[index];
            return Object.entries(prevObj).every(
              ([key, value]) => newObj[key] === value
            );
          });
        });

        if (isDuplicate) {
          return prevTranslations;
        } else {
          return [...prevTranslations, newTranslations];
        }
      });
      handleRadioChange(issueData.role || ROLE_FILTER.EMPLOYEE);
      issueData?.questions &&
        issueData.questions.map((item, index) =>
          setValue(`questions.${index}`, item)
        );
      issueData?.resourceId && setValue(`resourceId`, issueData?.resourceId);
    } else {
      const newTranslation = {
        title: "",
        description: "",
        language: "",
      };
      setValue(`translations.${0}`, newTranslation);
      handleRadioChange(ROLE_FILTER.HR);
    }
  }, [reset, setValue, issueData, handleRadioChange]);

  const renderStep1 = () => {
    return (
      <div className="col-lg-8 form-content step-1">
        <h3>{t("createIssue.title")}</h3>
        <span>{t("createIssue.subtitle")}</span>
        <div className="form-group mt-4">
          <label className="form-control-label ">
            {t("createLibrary.libraryData.domain.domains.title")}
          </label>
          <div className="my-4">
            {isLoading ? (
              <Skeleton
                count={3}
                inline
                height={30}
                containerClassName="container-radio-skeleton"
              />
            ) : (
              <CustomRadio
                options={options}
                selectedValue={selectedValue}
                {...register("role")}
                onChange={(value) => handleRadioChange(value as string)}
              />
            )}
          </div>
        </div>
        <div className="form-group row my-2">
          {isLoading ? (
            <Skeleton
              count={2}
              inline
              height={44}
              containerClassName="container-radio-skeleton"
            />
          ) : (
            <>
              <FormSelect
                typeErrors={
                  formState.errors?.categoryId &&
                  (formState.errors?.categoryId?.message as string)
                }
                isInvalid={formState.errors.hasOwnProperty("categoryId")}
                required
                defaultValue={getValues("categoryId")}
                wrapperClass="col-lg-7"
                label={t("createIssue.category.label")}
                placeholder={t("createIssue.category.placeholder")}
                options={categories}
                {...register(`categoryId`, {
                  required: {
                    value: true,
                    message: t("form-generic.error.required"),
                  },
                })}
                onChange={(selected) => {
                  clearErrors(`categoryId`);
                  setValue(`categoryId`, selected);
                }}
              />
              <FormSelect
                typeErrors={
                  formState.errors?.translations &&
                  (formState.errors?.severity?.message as string)
                }
                isInvalid={formState.errors.hasOwnProperty(`severity`)}
                required
                defaultValue={getValues("severity")}
                wrapperClass="col-lg-5"
                label={t("createIssue.severity.label")}
                placeholder={t("createIssue.severity.placeholder")}
                options={severity}
                {...register(`severity`, {
                  required: {
                    value: true,
                    message: t("form-generic.error.required"),
                  },
                })}
                onChange={(selected) => {
                  clearErrors("severity");
                  setValue("severity", selected as SEVERITY);
                }}
              />
            </>
          )}
        </div>
        <div className="form-group my-4 row">
          <h3>{t("createIssue.description.title")}</h3>
          <Translations
            translationsforIndex={translationsForIndex}
            handleAddLanguage={handleAddLanguage}
            removeLanguage={removeLanguage}
            setSelectedTranslations={setSelectedTranslations}
            isLoading={isLoading}
            formState={formState}
            populated={controls as IPopulated}
            register={register}
            unregister={unregister}
            clearErrors={clearErrors}
            setValue={setValue}
          />
        </div>
      </div>
    );
  };

  const renderStep2 = useMemo(() => {
    return currentStep === 2 ? (
      <Template
        selectedRoleFilter={controls.role}
        selectedFilterCateory={getValues("categoryId")}
        templateDataQuestions={getValues("questions")}
        onChange={(value) => setValue("questions", value)}
        templateDataName={getValues(`translations.${0}.title`)}
        arrayFilterSubCategory={arrayFilterSubCategory}
      />
    ) : null;
  }, [currentStep, controls.role, getValues, arrayFilterSubCategory, setValue]);
  const renderStep3 = useMemo(() => {
    return (
      <Step3Issue
        selectedRoleFilter={controls.role}
        selectedFilterCateory={getValues("categoryId")}
        resource={controls.resourceId as PopulatedResource}
        onChange={(value) => {
          value?.id !== controls.resourceId?.id
            ? setValue("resourceId", value)
            : unregister("resourceId");
        }}
        arrayFilterSubCategory={arrayFilterSubCategory}
      />
    );
  }, [
    arrayFilterSubCategory,
    controls.resourceId,
    controls.role,
    getValues,
    setValue,
    unregister,
  ]);

  return (
    <div
      className={` container-form-step ${getClasses({
        "custom-form-container": currentStep !== 2,
      })}${getClasses({
        "custom-form-template__step3": currentStep === 3,
      })} container-custom`}
    >
      <form className="form-content">
        <div className="container__top navbar fixed mb-4">
          <div className="container-step">
            <div
              onClick={() => {
                handleSubmit(onSumbitForm);
                setCurrentStep(1);
              }}
              className="step-info pointer"
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 1,
                })}`}
              >
                1
              </div>
              <span>{t("createIssue.issueFormStep.title1")}</span>
            </div>
            <div
              onClick={() => {
                if (currentStep !== 1) {
                  handleSubmit(onSumbitForm);
                  setCurrentStep(2);
                }
              }}
              className={`step-info ${getClasses({
                pointer: currentStep !== 1,
              })}`}
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 2,
                })}`}
              >
                2
              </div>
              <span className="label">
                {t("createIssue.issueFormStep.title2")}
              </span>
            </div>
            <div
              onClick={() => {
                if (currentStep === 3) {
                  handleSubmit(onSumbitForm);
                  setCurrentStep(3);
                }
              }}
              className={`step-info ${getClasses({
                pointer: currentStep === 3,
              })}`}
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 3,
                })} `}
              >
                3
              </div>
              <span>{t("createIssue.issueFormStep.title3")}</span>
            </div>
          </div>
          <div className="my-3">
            <Button
              disabled={
                currentStep === 2 &&
                !Boolean(getValues("questions")?.length > 0)
              }
              onClick={handleSubmit(onSubmit)}
            >
              {t(`createIssue.button.${currentStep !== 3 ? "go" : "save"}`)}
            </Button>
          </div>
        </div>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2}
        {(currentStep === 3 || currentStep === 4) && renderStep3}
      </form>
    </div>
  );
};

export default FormCreateIssue;
